/**
 * LOST Block
 * 
 * Warning system: "Why deals were lost"
 * Connects lost deals to active deals with same risk pattern
 * CTA: "Review similar active deals" / "Prevent this today"
 */

import { Deal } from '@/hooks/useDeals';
import { Lead } from '@/hooks/useLeads';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { XCircle, AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { useMemo } from 'react';
import { findSimilarRiskDeals } from '@/lib/patternRecognition';
import { useNavigate } from 'react-router-dom';

interface LostBlockProps {
  deals: Deal[];
  leads: Lead[];
}

const LOST_REASON_LABELS: Record<string, string> = {
  'no_budget': 'No Budget',
  'no_authority': 'No Authority',
  'no_need': 'No Need',
  'no_timeline': 'No Timeline',
  'price': 'Price',
  'competitor': 'Competitor',
  'timing': 'Timing',
  'time': 'Time / Inactivity',
  'ghosted': 'Ghosted',
  'no_fit': 'No Fit',
  'other': 'Other',
};

export function LostBlock({ deals, leads }: LostBlockProps) {
  const navigate = useNavigate();
  
  const lostData = useMemo(() => {
    const now = new Date();
    
    // Filter lost deals
    const lostDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      return dealStatus === 'lost' || d.stage === 'lost';
    });
    
    // Filter active deals (for pattern matching)
    const activeDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      return dealStatus !== 'won' && dealStatus !== 'lost' && d.stage !== 'won' && d.stage !== 'lost';
    });
    
    // Calculate metrics
    const totalLost = lostDeals.reduce((sum, d) => sum + (d.value || 0), 0);
    
    // Recent losses (last 10) with similar risk deals
    const recentLosses = lostDeals
      .sort((a, b) => {
        const lostA = (a as any).lost_at ? new Date((a as any).lost_at) : new Date(a.updated_at);
        const lostB = (b as any).lost_at ? new Date((b as any).lost_at) : new Date(b.updated_at);
        return lostB.getTime() - lostA.getTime();
      })
      .slice(0, 10)
      .map(lostDeal => {
        const lostLead = leads.find(l => l.id === lostDeal.lead_id);
        const similarDeals = findSimilarRiskDeals(lostDeal, lostLead, activeDeals, leads);
        return {
          deal: lostDeal,
          lead: lostLead,
          similarDeals,
        };
      });
    
    return {
      totalLost,
      count: lostDeals.length,
      recentLosses,
    };
  }, [deals, leads]);
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-destructive/60" />
          <h2 className="text-xl font-bold text-foreground">LOST</h2>
          <Badge variant="secondary" className="text-xs font-mono">
            {lostData.count}
          </Badge>
        </div>
        <div className="text-lg font-semibold text-muted-foreground">
          {formatCurrency(lostData.totalLost)} lost
        </div>
      </div>
      
      {/* Why deals were lost */}
      {lostData.recentLosses.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-destructive" />
            Why deals were lost
          </h3>
          
          <div className="space-y-2">
            {lostData.recentLosses.map(({ deal, lead, similarDeals }) => {
              const lostAt = (deal as any).lost_at ? new Date((deal as any).lost_at) : new Date(deal.updated_at);
              const lostReason = (deal as any).lost_reason || 'other';
              const reasonLabel = LOST_REASON_LABELS[lostReason] || 'Other';
              
              // Calculate inactivity before loss
              const lastContact = lead?.last_interaction_at ? new Date(lead.last_interaction_at) : null;
              const daysInactive = lastContact ? differenceInDays(lostAt, lastContact) : null;
              
              return (
                <Card key={deal.id} className="border-destructive/10 bg-destructive/5">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-foreground">
                            {lead?.name || deal.lead?.name || 'Unknown'}
                          </p>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-destructive/30 text-destructive">
                            {reasonLabel}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Lost on {format(lostAt, 'MMM d, yyyy')}
                        </p>
                        <p className="text-xs text-muted-foreground/70 italic mb-1">
                          Why it was lost: {reasonLabel}
                        </p>
                        {daysInactive !== null && daysInactive > 0 && (
                          <p className="text-xs text-muted-foreground/70 italic">
                            This deal was lost after {daysInactive} day{daysInactive > 1 ? 's' : ''} of inactivity.
                          </p>
                        )}
                      </div>
                      <div className="text-base font-semibold text-muted-foreground">
                        {formatCurrency(deal.value || 0)}
                      </div>
                    </div>
                    
                    {/* Similar risk deals warning */}
                    {similarDeals.length > 0 && (
                      <div className="pt-2 border-t border-destructive/10">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-destructive">
                            {similarDeals.length} active deal{similarDeals.length > 1 ? 's' : ''} show{similarDeals.length === 1 ? 's' : ''} the same risk pattern
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate('/app/today')}
                            className="h-6 px-2 text-xs gap-1"
                          >
                            Prevent this today
                            <ArrowRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {lostData.count === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No deals lost yet
        </div>
      )}
    </div>
  );
}

